import type { TournamentView } from './tournament';

/** An empty seat past the players: whoever meets it goes through without playing. */
export const BYE: unique symbol = Symbol('bye');
/** A side of a match: a player's name, null while unknown (a seat not filled, a match not played yet), or a bye. */
export type BracketSide = string | null | typeof BYE;

/**
 * `waiting`: a side is still unknown; `ready`: both known, not started; `live`: being played; `done`: won; `bye`: one
 * side is a bye, the other goes through (drawn as an empty place).
 */
export type BracketState = 'waiting' | 'ready' | 'live' | 'done' | 'bye';

export interface BracketMatch {
  id: string;
  sides: [BracketSide, BracketSide];
  score: [number, number] | null;
  state: BracketState;
  /** The side that goes through: once `done`, or at once past a bye. */
  won: 0 | 1 | null;
}

export interface Bracket {
  /** Round by round, from the first. */
  winners: BracketMatch[][];
  losers: BracketMatch[][];
  /** The grand final, and its reset once the losers bracket's player has won it. */
  final: BracketMatch[];
  champion: string | null;
}

/** Seeds 1..n (a power of two) in bracket order: 1 meets n first, and the top two seeds meet only in the final. */
export function seedOrder(n: number): number[] {
  let order = [1];
  while (order.length < n) {
    const sum = order.length * 2 + 1;
    order = order.flatMap((s) => [s, sum - s]);
  }
  return order;
}

const winner = (m: BracketMatch): BracketSide => (m.won === null ? null : m.sides[m.won]);
const loser = (m: BracketMatch): BracketSide => (m.won === null ? null : m.sides[m.won === 0 ? 1 : 0]);

/**
 * A region's double elimination bracket from its seeded players and results (ADR-0137): the winners bracket, the
 * losers bracket its first losses drop into, and the grand final, with a reset when the losers bracket's player wins it.
 */
export function drawBracket(t: TournamentView): Bracket {
  let size = 4;
  while (size < t.players.length) size *= 2;
  const seat = (seed: number): BracketSide => (seed <= t.players.length ? (t.players[seed - 1] ?? null) : BYE);

  const play = (id: string, a: BracketSide, b: BracketSide): BracketMatch => {
    const sides: [BracketSide, BracketSide] = [a, b];
    if (a === BYE || b === BYE) return { id, sides, score: null, state: 'bye', won: a === BYE ? 1 : 0 };
    const result = a !== null && b !== null ? t.results[id] : undefined;
    if (!result) return { id, sides, score: null, state: a !== null && b !== null ? 'ready' : 'waiting', won: null };
    const [top, bottom] = result.score;
    if (result.live || top === bottom) return { id, sides, score: result.score, state: 'live', won: null };
    return { id, sides, score: result.score, state: 'done', won: top > bottom ? 0 : 1 };
  };
  /** Round `name`: its matches pair the previous round's two by two. */
  const halve = (name: string, round: BracketMatch[], side: (m: BracketMatch) => BracketSide) =>
    Array.from({ length: round.length / 2 }, (_, j) =>
      play(`${name}-${j + 1}`, side(round[2 * j] as BracketMatch), side(round[2 * j + 1] as BracketMatch)),
    );

  const order = seedOrder(size);
  const first = Array.from({ length: size / 2 }, (_, j) =>
    play(`W1-${j + 1}`, seat(order[2 * j] ?? 0), seat(order[2 * j + 1] ?? 0)),
  );
  const winners = [first];
  let up = first;
  while (up.length > 1) {
    up = halve(`W${winners.length + 1}`, up, winner);
    winners.push(up);
  }

  let down = halve('L1', first, loser);
  const losers = [down];
  for (let r = 1; r < winners.length; r++) {
    const dropping = winners[r] as BracketMatch[];
    const n = dropping.length;
    // Who drops in meets someone from the other side of the bracket: reversed, then halves swapped, in turn, so an
    // early rematch is rare.
    const from = (j: number) => (r % 2 === 1 ? n - 1 - j : (j + (n >> 1)) % n);
    const name = `L${losers.length + 1}`;
    down = down.map((m, j) => play(`${name}-${j + 1}`, winner(m), loser(dropping[from(j)] as BracketMatch)));
    losers.push(down);
    if (n > 1) {
      down = halve(`L${losers.length + 1}`, down, winner);
      losers.push(down);
    }
  }

  const top = winner(up[0] as BracketMatch);
  const bottom = winner(down[0] as BracketMatch);
  const final = [play('GF', top, bottom)];
  if (final[0]?.state === 'done' && final[0].won === 1) final.push(play('GF2', top, bottom));
  const champion = winner(final.at(-1) as BracketMatch);
  return { winners, losers, final, champion: typeof champion === 'string' ? champion : null };
}
